import { useCallback, useMemo } from "react";
import { ViewModeToggle } from "#app/components/view-options/view-mode-toggle";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";

export type FormsViewMode = "cards" | "table";

export type FormsViewScope = "directory" | "project";

const STORAGE_KEY = "forms.view-mode.chosen";

const DEFAULT_MODES: Record<FormsViewScope, FormsViewMode> = {
  directory: "table",
  project: "table",
};

function isViewMode(value: unknown): value is FormsViewMode {
  return value === "cards" || value === "table";
}

const NOTHING_CHOSEN: Partial<Record<FormsViewScope, FormsViewMode>> = {};

export function useFormsViewMode(scope: FormsViewScope): {
  mode: FormsViewMode;
  setMode: (mode: FormsViewMode) => void;
} {
  const { state, update } = usePersistedState(STORAGE_KEY, NOTHING_CHOSEN);
  const stored: unknown = state[scope];
  const mode = isViewMode(stored) ? stored : DEFAULT_MODES[scope];
  const setMode = useCallback(
    (next: FormsViewMode) => update({ [scope]: next }),
    [scope, update],
  );
  return useMemo(() => ({ mode, setMode }), [mode, setMode]);
}

const FORMS_VIEW_MODES = [
  { mode: "cards", label: "Cards", icon: "layout-grid" },
  { mode: "table", label: "Table", icon: "table" },
] as const;

export function FormsViewModeToggle({
  mode,
  onModeChange,
}: {
  mode: FormsViewMode;
  onModeChange: (mode: FormsViewMode) => void;
}) {
  return <ViewModeToggle mode={mode} modes={FORMS_VIEW_MODES} onModeChange={onModeChange} />;
}
